/**
 * Text of a bell item (pure, unit-tested): "Bjarki og Ingólfur skrifuðu á Æfing (fim. 23. okt.)",
 * "Jón skrifaði í spjall Tenór 1", "Bjarki, Ingólfur og 3 aðrir skrifuðu í kórspjallið".
 */
import type { ChatId } from '../../shared/chats';
import type { NotificationItemDTO } from '../../shared/dto/chat';
import type { T } from '../i18n/translate';
import { formatShortDay } from '../lib/dates';

/** Names written out in full; with more authors: the first NAMES_BEFORE_OTHERS, then "og N aðrir". */
export const MAX_NAMES = 3;
const NAMES_BEFORE_OTHERS = 2;

function firstName(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] || fullName.trim();
}

/** First names — but the full name for authors who share their first name with another listed author. */
export function displayNames(fullNames: readonly string[]): string[] {
  const firsts = fullNames.map(firstName);
  const seen = new Map<string, number>();
  for (const f of firsts) seen.set(f, (seen.get(f) ?? 0) + 1);
  return fullNames.map((n, i) => ((seen.get(firsts[i]!) ?? 0) > 1 ? n.trim() : firsts[i]!));
}

/** "Bjarki", "Bjarki og Ingólfur", "Bjarki, Ingólfur og Jón", "Bjarki, Ingólfur og 3 aðrir". */
export function joinNames(fullNames: readonly string[], total: number, t: T): string {
  const and = t('notify.and');
  if (total > MAX_NAMES) {
    const shown = displayNames(fullNames.slice(0, NAMES_BEFORE_OTHERS));
    return `${shown.join(', ')} ${and} ${t('notify.others', { count: total - shown.length })}`;
  }
  const names = displayNames(fullNames.slice(0, total));
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} ${and} ${names[names.length - 1]}`;
}

/** Name of a chat as the object of "skrifaði í …": "kórspjallið", "spjall Tenór 1". */
export function chatInLabel(id: ChatId | string, t: T): string {
  if (id === 'choir') return t('notify.in_choir');
  return t('notify.in_voice', { voice: t(`section.${id as Exclude<ChatId, 'choir'>}`) });
}

/** The item's one-line title. */
export function notificationTitle(item: NotificationItemDTO, t: T, loc: string): string {
  const names = joinNames(
    item.authors.map((a) => a.fullName),
    item.authorCount,
    t,
  );
  const many = item.authorCount > 1;
  if (item.kind === 'event') {
    const ev = item.event;
    const label = ev ? t('notify.event_label', { title: ev.title, day: formatShortDay(ev.startsAt, loc) }) : '';
    return t(many ? 'notify.wrote_event_many' : 'notify.wrote_event_one', { names, event: label });
  }
  return t(many ? 'notify.wrote_chat_many' : 'notify.wrote_chat_one', { names, chat: chatInLabel(item.id, t) });
}

/** Where tapping the item goes: the event's message board, or the chat. */
export function notificationHref(item: Pick<NotificationItemDTO, 'kind' | 'id'>): string {
  return item.kind === 'event' ? `/events/${encodeURIComponent(item.id)}#spjall` : `/chat/${encodeURIComponent(item.id)}`;
}

/** Badge text: "1" … "99", then "99+". */
export function badgeText(n: number): string {
  return n > 99 ? '99+' : String(n);
}
